import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import TermsModal from './TermsModal';

/**
 * TermsGuard Component
 * Wraps authenticated routes and ensures user has accepted current T&C.
 * Shows a mandatory acceptance modal if terms need to be accepted.
 */
const TermsGuard = ({ children }) => {
  const { token, isAuthenticated, logout } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [termsInfo, setTermsInfo] = useState(null);

  useEffect(() => {
    if (isAuthenticated && token) {
      checkTermsAcceptance();
    } else {
      setChecking(false);
    }
  }, [isAuthenticated, token]);

  const checkTermsAcceptance = async () => {
    try {
      const response = await apiClient.get('/api/terms/check');

      if (response.success) {
        if (response.needs_acceptance) {
          setNeedsAcceptance(true);
          setTermsInfo({
            version: response.current_version,
            title: response.terms_title,
            summary: response.terms_summary,
            reason: response.reason
          });
        } else {
          setNeedsAcceptance(false);
        }
      }
    } catch (error) {
      console.error('Error checking terms acceptance:', error);
      // On error, allow access (fail open) - the backend will still validate
      setNeedsAcceptance(false);
    } finally {
      setChecking(false);
    }
  };

  const handleAccept = (version) => {
    setNeedsAcceptance(false);
    setTermsInfo(null);
  };

  const handleDecline = () => {
    // If user declines, log them out
    logout();
  };

  // Show loading while checking
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying account...</p>
        </div>
      </div>
    );
  }

  // Show terms modal if acceptance needed
  if (needsAcceptance && termsInfo) {
    return (
      <>
        {/* Blurred background */}
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 filter blur-sm">
          {children}
        </div>

        {/* Terms Modal - Cannot be closed without accepting */}
        <TermsModal
          isOpen={true}
          onClose={handleDecline}
          onAccept={handleAccept}
          version={termsInfo.version}
          showAcceptButton={true}
          title={termsInfo.title || "Terms and Conditions"}
          requireScroll={true}
        />

        {/* Decline option */}
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <button
            onClick={handleDecline}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
          >
            Decline and Sign Out
          </button>
        </div>
      </>
    );
  }

  // Render children if no acceptance needed
  return children;
};

export default TermsGuard;
